import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Avatar from '@mui/material/Avatar'
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import LoginProblemLinks from './LoginProblemLinks'
import { formatDeviseErrors, splitFieldErrors, fieldErrorText } from './deviseErrors'
import { deviseFetch, showFailedRequest } from './deviseRequest'
import useSubmitting from '../../hooks/useSubmitting'

import {
  setFlashNotice,
  setFlashAlert,
  clearFlashNotice,
  clearFlashAlert,
} from '../../redux/features/ui/uiSlice'
import { selectAuthToken, setAuthToken } from '../../redux/features/user/userSlice'

// Ask Devise to email the unlock link for a locked account
// (POST /user/unlock.json).
export default function ResendUnlock() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [errors, setErrors] = useState({})
  const [submitting, submit] = useSubmitting()
  const storedAuthToken = useSelector(selectAuthToken)
  const csrfToken = storedAuthToken || document.querySelector('meta[name=csrf-token]')?.content || ''

  useEffect(() => {
    if (!storedAuthToken) {
      const documentAuthToken = document.querySelector('meta[name=csrf-token]')?.content
      if (documentAuthToken) {
        dispatch(setAuthToken(documentAuthToken))
      }
    }
  }, [dispatch, storedAuthToken])

  const handleSubmit = (evt) => {
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)
    if (csrfToken) {
      data.set('authenticity_token', csrfToken)
    }

    submit(async () => {
      const result = await deviseFetch(dispatch, "/user/unlock.json", {
        method: 'post',
        body: data,
        headers: {
          'Accept': 'application/json',
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        }
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      if (response.status === 201) {
        dispatch(setFlashNotice(
          'Check your inbox. If that account is locked, we have sent unlock instructions.'
        ))
        navigate('/app/sign_in')
        return
      }

      if (response.status === 422) {
        // Errors beside the fields, with a summary in the flash.
        const { fieldErrors, flashMessage } = splitFieldErrors(
          json_data, ['email'], 'the unlock instructions from being sent'
        )
        setErrors(fieldErrors)
        dispatch(setFlashAlert(flashMessage))
        return
      }

      dispatch(setFlashAlert(formatDeviseErrors(json_data)))
    })
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 9,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component='h1' variant='h5'>
          Resend unlock instructions
        </Typography>
        <Box
          component="form"
          sx={{ mt: 1, width: '100%', maxWidth: 400 }}
          onSubmit={handleSubmit}
          noValidate
        >
          <TextField
            margin='normal'
            required
            fullWidth
            id='user[email]'
            label='Email'
            name='user[email]'
            autoComplete='email'
            error={Boolean(errors.email)}
            helperText={fieldErrorText(errors, 'email')}
            autoFocus
          />
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            Send unlock instructions
          </Button>
          <LoginProblemLinks />
        </Box>
      </Box>
    </Container>
  )
}
